import * as D from '@radix-ui/react-dialog';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * Dialog — la interrupción, reservada para cuando de verdad hace falta.
 *
 * Dos casos y ninguno más:
 *   · Confirmar algo irreversible (borrar, rechazar, enviar a toda la empresa).
 *   · Una barrera legal que bloquea de verdad (Habeas Data).
 *
 * Todo lo demás —ver un detalle, editar una fila, elegir en una lista— es
 * `Drawer`, edición en línea o página propia. Si al escribir un diálogo la
 * respuesta a «¿puede el usuario seguir necesitando ver lo de detrás?» es sí,
 * no era un diálogo.
 *
 * `ConfirmDialog` de abajo cubre el caso frecuente. Deja la acción destructiva
 * a la derecha y la de cancelar a la izquierda, y **el foco arranca en
 * Cancelar**: cuando la tecla más probable es Enter, el destino por defecto
 * tiene que ser la salida segura.
 */
export function Dialog({
  abierto, onCambio, titulo, descripcion, pie, children, ancho = 'max-w-lg',
}: {
  abierto: boolean;
  onCambio: (v: boolean) => void;
  titulo: string;
  descripcion?: string;
  pie?: React.ReactNode;
  children?: React.ReactNode;
  ancho?: string;
}) {
  return (
    <D.Root open={abierto} onOpenChange={onCambio}>
      <D.Portal>
        <D.Overlay className="anim-velo fixed inset-0 z-modal bg-brand/45" />
        <D.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-modal w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            // El alto, acotado a la ventana, y el panel en columna. Sin esto el
            // diálogo crecía con su contenido y se salía por ARRIBA, que es la
            // forma mala de salirse: está centrado con `-translate-y-1/2`, así
            // que lo que sobra se reparte a partes iguales entre arriba y abajo,
            // y como es `fixed` lo de arriba **no se puede alcanzar de ninguna
            // manera** — no hay scroll que lo traiga.
            //
            // Medido el 2026-10-05 en la ficha del Directorio, con una persona
            // con siete cursos: panel de 1.104 px en una ventana de 720, con su
            // borde superior en −761 px. La foto y el nombre no se veían, y
            // `max-height` era `none` y `overflow-y` era `visible`.
            //
            // `2rem` para que la holgura de arriba y abajo sea la misma que la
            // de los lados, que ya estaba en `w-[calc(100vw-2rem)]`. Y `dvh` y
            // no `vh` porque en un móvil la barra del navegador se mete dentro
            // de `vh`: es la unidad que ya usa `Drawer` para su variante de
            // abajo.
            'flex max-h-[calc(100dvh-2rem)] flex-col',
            'rounded-lg border border-line bg-surface shadow-overlay focus:outline-none',
            'anim-dialogo',
            ancho,
          )}
        >
          <header className="flex items-start gap-3 px-5 pb-3 pt-4">
            <div className="min-w-0 flex-1">
              {/* `[overflow-wrap:anywhere]` en título y descripción: los dos
                  pueden traer texto que escribió una persona. Una palabra sin
                  espacios —una URL, un código— no partía y se pintaba FUERA del
                  diálogo. Medido el 2026-09-16 en la vista previa de la
                  Biblioteca: 717 px de texto en una caja de 642. Solo cambia
                  algo donde el texto ya desbordaba. */}
              <D.Title className="text-5 font-bold leading-tight text-brand-ink [overflow-wrap:anywhere]">{titulo}</D.Title>
              {descripcion && (
                <D.Description className="mt-1.5 text-3 leading-relaxed text-ink-2 [overflow-wrap:anywhere]">
                  {descripcion}
                </D.Description>
              )}
            </div>
            <D.Close asChild>
              <Button variant="ghost" size="sm" iconOnly aria-label="Cerrar">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </Button>
            </D.Close>
          </header>

          {/* El cuerpo es lo único que se desplaza, igual que en `Drawer`:
              cabecera y pie quedan anclados, así que el botón de cerrar y la
              acción principal nunca se van de la vista por mucho contenido que
              haya. Desplazar el panel ENTERO habría arreglado el recorte y roto
              eso, que es peor.

              `min-h-0` no es decorativo: un hijo de flex no se encoge por
              debajo de su contenido sin él, así que sin esa clase el panel
              volvería a crecer y el `overflow-y` no llegaría a usarse nunca. */}
          {children && <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2">{children}</div>}

          {pie && (
            <footer className="flex flex-wrap justify-end gap-2 px-5 pb-4 pt-3">{pie}</footer>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function ConfirmDialog({
  abierto, onCambio, titulo, descripcion, textoConfirmar = 'Confirmar',
  destructivo, onConfirmar, cargando,
}: {
  abierto: boolean;
  onCambio: (v: boolean) => void;
  titulo: string;
  /** Qué va a pasar exactamente y si se puede deshacer. «¿Estás seguro?» no es
   *  una descripción: no aporta ni un dato que el usuario no tuviera ya. */
  descripcion: string;
  textoConfirmar?: string;
  destructivo?: boolean;
  onConfirmar: () => void;
  cargando?: boolean;
}) {
  return (
    <Dialog
      abierto={abierto}
      onCambio={onCambio}
      titulo={titulo}
      descripcion={descripcion}
      ancho="max-w-md"
      pie={
        <>
          {/* autoFocus en la salida segura, no en la acción. */}
          <Button variant="ghost" onClick={() => onCambio(false)} autoFocus>
            Cancelar
          </Button>
          <Button
            variant={destructivo ? 'destructive' : 'primary'}
            loading={cargando}
            onClick={onConfirmar}
          >
            {textoConfirmar}
          </Button>
        </>
      }
    />
  );
}
